import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';
import { Can } from '../../auth/Can.js';

export function Sidebar() {
  const { t } = useTranslation(['settings', 'common', 'bookkeeping']);
  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `block rounded px-3 py-1.5 text-sm ${
      isActive ? 'bg-blue-100 text-blue-900' : 'text-slate-700 hover:bg-slate-100'
    }`;
  return (
    <aside className="w-56 border-r bg-white p-4">
      <nav className="space-y-1">
        <NavLink to="/app" end className={linkClass}>
          {t('common:dashboard', { defaultValue: 'Dashboard' })}
        </NavLink>

        <div className="mt-4 px-3 text-xs uppercase tracking-wide text-slate-400">
          {t('bookkeeping:title')}
        </div>
        <NavLink to="/app/bookkeeping/accounts" className={linkClass}>
          {t('bookkeeping:nav.accounts')}
        </NavLink>
        <NavLink to="/app/bookkeeping/customers" className={linkClass}>
          {t('bookkeeping:nav.customers')}
        </NavLink>
        <NavLink to="/app/bookkeeping/vendors" className={linkClass}>
          {t('bookkeeping:nav.vendors')}
        </NavLink>
        <NavLink to="/app/bookkeeping/items" className={linkClass}>
          {t('bookkeeping:nav.items')}
        </NavLink>
        <NavLink to="/app/bookkeeping/journal-entries" className={linkClass}>
          {t('bookkeeping:nav.journalEntries')}
        </NavLink>
        <NavLink to="/app/bookkeeping/reports/trial-balance" className={linkClass}>
          {t('bookkeeping:nav.trialBalance')}
        </NavLink>
        <NavLink to="/app/bookkeeping/reports/income-statement" className={linkClass}>
          {t('bookkeeping:nav.incomeStatement')}
        </NavLink>
        <NavLink to="/app/bookkeeping/reports/balance-sheet" className={linkClass}>
          {t('bookkeeping:nav.balanceSheet')}
        </NavLink>
        <NavLink to="/app/bookkeeping/reports/general-ledger" className={linkClass}>
          {t('bookkeeping:nav.generalLedger')}
        </NavLink>
        <NavLink to="/app/bookkeeping/reports/ats" className={linkClass}>
          {t('bookkeeping:nav.ats')}
        </NavLink>
        <NavLink to="/app/bookkeeping/reports/f104" className={linkClass}>
          {t('bookkeeping:nav.f104')}
        </NavLink>
        <NavLink to="/app/bookkeeping/reports/f103" className={linkClass}>
          {t('bookkeeping:nav.f103')}
        </NavLink>

        <div className="mt-3 px-3 text-xs uppercase tracking-wide text-slate-400">
          {t('bookkeeping:nav.sales_section')}
        </div>
        <NavLink to="/app/bookkeeping/sales/invoices" className={linkClass}>
          {t('bookkeeping:nav.invoices')}
        </NavLink>
        <NavLink to="/app/bookkeeping/sales/sales-receipts" className={linkClass}>
          {t('bookkeeping:nav.sales_receipts')}
        </NavLink>
        <NavLink to="/app/bookkeeping/sales/receive-payments" className={linkClass}>
          {t('bookkeeping:nav.receive_payments')}
        </NavLink>
        <NavLink to="/app/bookkeeping/sales/tax-rates" className={linkClass}>
          {t('bookkeeping:nav.tax_rates')}
        </NavLink>
        <NavLink to="/app/bookkeeping/sales/withholding-rates" className={linkClass}>
          {t('bookkeeping:nav.withholding_rates')}
        </NavLink>
        <NavLink to="/app/bookkeeping/sales/credit-memos" className={linkClass}>
          {t('bookkeeping:nav.credit_memos')}
        </NavLink>
        <NavLink to="/app/bookkeeping/sales/nota-debito/new" className={linkClass}>
          {t('bookkeeping:nav.notaDebito')}
        </NavLink>
        <NavLink to="/app/bookkeeping/sales/guia-remision/new" className={linkClass}>
          {t('bookkeeping:nav.guiaRemision')}
        </NavLink>

        <div className="mt-3 px-3 text-xs uppercase tracking-wide text-slate-400">
          {t('bookkeeping:nav.purchases_section')}
        </div>
        <NavLink to="/app/bookkeeping/purchases/bills" className={linkClass}>
          {t('bookkeeping:nav.bills')}
        </NavLink>
        <NavLink to="/app/bookkeeping/purchases/expenses" className={linkClass}>
          {t('bookkeeping:nav.expenses')}
        </NavLink>
        <NavLink to="/app/bookkeeping/purchases/pay-bills" className={linkClass}>
          {t('bookkeeping:nav.pay_bills')}
        </NavLink>
        <NavLink to="/app/bookkeeping/purchases/checks" className={linkClass}>
          {t('bookkeeping:nav.checks')}
        </NavLink>
        <NavLink to="/app/bookkeeping/purchases/liquidacion-compra/new" className={linkClass}>
          {t('bookkeeping:nav.liquidacionCompra')}
        </NavLink>

        <div className="mt-3 px-3 text-xs uppercase tracking-wide text-slate-400">
          {t('bookkeeping:nav.inventory_section')}
        </div>
        <NavLink to="/app/bookkeeping/inventory/warehouses" className={linkClass}>
          {t('bookkeeping:nav.warehouses')}
        </NavLink>
        <NavLink to="/app/bookkeeping/inventory/adjustments" className={linkClass}>
          {t('bookkeeping:nav.adjustments')}
        </NavLink>
        <NavLink to="/app/bookkeeping/inventory/kardex" className={linkClass}>
          {t('bookkeeping:nav.kardex')}
        </NavLink>
        <NavLink to="/app/bookkeeping/inventory/stock" className={linkClass}>
          {t('bookkeeping:nav.stock')}
        </NavLink>

        <Can I="read" a="Account">
          <div className="mt-3 px-3 text-xs uppercase tracking-wide text-slate-400">
            {t('bookkeeping:nav.banking_section')}
          </div>
          <NavLink to="/app/banking" className={linkClass}>
            {t('bookkeeping:nav.bankTransactions')}
          </NavLink>
          <NavLink to="/app/banking/rules" className={linkClass}>
            {t('bookkeeping:nav.bankRules')}
          </NavLink>
          <NavLink to="/app/banking/reconcile" className={linkClass}>
            {t('bookkeeping:nav.bankReconcile')}
          </NavLink>
        </Can>

        <div className="mt-4 px-3 text-xs uppercase tracking-wide text-slate-400">
          {t('common:settings', { defaultValue: 'Settings' })}
        </div>
        <NavLink to="/app/settings/profile" className={linkClass}>
          {t('profile')}
        </NavLink>
        <NavLink to="/app/settings/security" className={linkClass}>
          {t('security')}
        </NavLink>
        <Can I="read" a="Member">
          <NavLink to="/app/settings/members" className={linkClass}>
            {t('members')}
          </NavLink>
        </Can>
        <Can I="manage" a="Invitation">
          <NavLink to="/app/settings/invitations" className={linkClass}>
            {t('invitations')}
          </NavLink>
        </Can>
        <NavLink to="/app/settings/sri-certificate" className={linkClass}>
          {t('bookkeeping:nav.sriCertificate')}
        </NavLink>
        <NavLink to="/app/settings/emission-points" className={linkClass}>
          {t('bookkeeping:nav.emissionPoints')}
        </NavLink>
      </nav>
    </aside>
  );
}
